/**
 * Web streaming-text companions — the twin of `streaming-text.tsx`.
 *
 * The prose itself is rendered by assistant-ui's markdown primitive; what lives
 * here is everything around it:
 *
 *   - `rehypeStreamWords`  wraps each word of a streaming block in a span so
 *                          new words resolve out of blur (`.tabbio-word`).
 *                          Splitting on whitespace is safe for Arabic: letters
 *                          only join *within* a word, never across a space.
 *   - `MarkdownLinkChip`   renders a link in the answer as an inline source
 *                          chip instead of a bare underlined URL.
 *   - `SourcesToggle` /
 *     `SourcesList`        the avatar stack + "N sources" control and the
 *                          expandable list it opens.
 *   - `FollowUpList`       the follow-up prompts, as a stacked list.
 */
import { useMemo, type AnchorHTMLAttributes, type CSSProperties, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAui } from '@assistant-ui/react';
import { getFollowUpSuggestions } from './follow-up-suggestions';
import {
  SOURCE_TONES,
  sourceInitials,
  sourceShowsDomain,
  type StreamingSource,
} from './streaming-sources';

// ---------------------------------------------------------------------------
// Word reveal
// ---------------------------------------------------------------------------

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
  properties?: Record<string, unknown>;
};

const SKIP_TAGS = new Set(['code', 'pre', 'script', 'style']);

function wrapWords(node: HastNode) {
  if (!node.children) return;
  if (node.type === 'element' && node.tagName && SKIP_TAGS.has(node.tagName)) return;

  const next: HastNode[] = [];
  for (const child of node.children) {
    if (child.type === 'text' && typeof child.value === 'string') {
      for (const token of child.value.split(/(\s+)/)) {
        if (!token) continue;
        if (/^\s+$/.test(token)) {
          next.push({ type: 'text', value: token });
        } else {
          next.push({
            type: 'element',
            tagName: 'span',
            properties: { className: ['tabbio-word'] },
            children: [{ type: 'text', value: token }],
          });
        }
      }
      continue;
    }
    wrapWords(child);
    next.push(child);
  }
  node.children = next;
}

/** rehype plugin: `<span class="tabbio-word">` around every word outside code. */
export function rehypeStreamWords() {
  return (tree: HastNode) => {
    wrapWords(tree);
  };
}

/** Stable reference: react-markdown rebuilds its pipeline when this changes. */
export const STREAMING_REHYPE_PLUGINS = [rehypeStreamWords];

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

function domainOf(href: string | undefined): string | null {
  if (!href) return null;
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * A source mark: a small tinted disc with a globe, cycling through the three
 * muted tones so a stack of sources reads as distinct without shouting. The
 * source's initials stay in the accessible name of the row, not in the disc.
 */
export const SourceAvatar: FC<{ source: StreamingSource; large?: boolean; index?: number }> = ({
  source,
  large,
  index = 0,
}) => (
  <span
    className={`tabbio-source-avatar is-${SOURCE_TONES[index % SOURCE_TONES.length]}${large ? ' is-large' : ''}`}
    aria-hidden
    title={sourceInitials(source)}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.5 12h17M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  </span>
);

/** A link inside the answer, drawn as an inline chip with the site's mark. */
export const MarkdownLinkChip: FC<AnchorHTMLAttributes<HTMLAnchorElement>> = ({
  href,
  children,
  ...rest
}) => {
  const domain = domainOf(href);
  if (!domain) {
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    );
  }
  const source: StreamingSource = { href: href!, domain, name: domain };
  return (
    <a
      {...rest}
      href={href}
      target="_blank"
      rel="noreferrer"
      className="tabbio-cite-chip"
      title={domain}
    >
      <SourceAvatar source={source} />
      <span className="tabbio-cite-chip-text">{children}</span>
    </a>
  );
};

export const SourcesToggle: FC<{
  sources: StreamingSource[];
  open: boolean;
  onToggle: () => void;
}> = ({ sources, open, onToggle }) => {
  const { t } = useTranslation('common');
  if (sources.length === 0) return null;
  return (
    <button
      type="button"
      className="tabbio-sources-toggle"
      aria-expanded={open}
      onClick={onToggle}
    >
      <span className="tabbio-source-stack">
        {sources.slice(0, 3).map((source, index) => (
          <SourceAvatar key={source.href} source={source} index={index} />
        ))}
      </span>
      <span>{t('chat.sources.count', { count: sources.length })}</span>
    </button>
  );
};

export const SourcesList: FC<{ sources: StreamingSource[]; open: boolean }> = ({ sources, open }) => {
  if (sources.length === 0) return null;
  return (
    <div className={`tabbio-sources-body${open ? ' is-open' : ''}`} aria-hidden={!open}>
      <div>
        <div className="tabbio-sources-list">
          {sources.map((source, index) => (
            <a
              key={source.href}
              href={source.href}
              target="_blank"
              rel="noreferrer"
              className="tabbio-source-row"
              tabIndex={open ? 0 : -1}
            >
              <span className="tabbio-source-row-mark" aria-hidden>
                <SourceAvatar source={source} large index={index} />
              </span>
              <span className="tabbio-source-row-text">
                <span className="tabbio-source-row-name" dir="auto">
                  {source.name}
                </span>
                {sourceShowsDomain(source) ? (
                  <span className="tabbio-source-row-domain" dir="ltr">
                    {source.domain}
                  </span>
                ) : null}
              </span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Follow-ups
// ---------------------------------------------------------------------------

export const FollowUpList: FC<{ toolName?: string }> = ({ toolName }) => {
  const { t } = useTranslation('seeker');
  const { t: tc } = useTranslation('common');
  const aui = useAui();
  const suggestions = useMemo(() => getFollowUpSuggestions(toolName), [toolName]);

  if (suggestions.length === 0) return null;

  return (
    <div className="tabbio-followups">
      <p className="tabbio-followups-title">{tc('chat.followUps.title')}</p>
      <div className="tabbio-followups-list">
        {suggestions.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className="tabbio-followup-row"
            style={{ ['--row-delay' as string]: `${index * 90}ms` } as CSSProperties}
            onClick={() => aui.thread().append(item.prompt)}
          >
            <span className="tabbio-followup-row-icon" aria-hidden>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabbio-rtl-flip">
                <path d="M9 10l-5 5 5 5" />
                <path d="M20 4v7a4 4 0 0 1-4 4H4" />
              </svg>
            </span>
            <span dir="auto">{t(item.titleKey)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
